import { useState } from "react";
import { useTranslation } from "react-i18next";

import LazyImage from "@/my_components/Common/LazyImage";
import TutorApplicationModal from "@/my_components/Common/TutorApplicationModal";
import { image_url } from "@/helper/Utilities";

const POINT_ICONS = ["feather-award", "feather-globe", "feather-link-2", "feather-check-circle"];
const POINT_KEYS = ["credit", "reach", "mention", "quality"];

/**
 * Homepage pitch recruiting tutors/content creators -- same spirit as freeCodeCamp's "contribute
 * a course" pitch to YouTube educators: SmartTech Academy helps people worldwide upskill for
 * free, and a tutor who builds quality content here gets credit plus growing exposure, with one
 * controlled, built-in spot to point learners back to their own work (never an open ad slot).
 * "Apply to teach" opens TutorApplicationModal.js, which posts to tutorApplicationService.js.
 */
const TutorRecruitment = () => {
  const { t } = useTranslation("home");
  const [modalOpen, setModalOpen] = useState(false);

  const points = POINT_KEYS.map((key, i) => ({
    key,
    icon: POINT_ICONS[i],
    label: t(`tutorRecruitment.points.${key}.label`),
    desc: t(`tutorRecruitment.points.${key}.desc`),
  }));

  return (
    <div className="rbt-tutor-recruit bg-color-white">
      <div className="container">
        <div className="rbt-tutor-recruit__grid">
          <div className="rbt-tutor-recruit__visual">
            <div className="rbt-tutor-recruit__image-frame">
              <LazyImage
                src={image_url("/app_images/hero/hero-student.jpg")}
                alt={t("tutorRecruitment.imageAlt", "A tutor preparing course material")}
                fill
                sizes="(max-width: 767px) 90vw, 45vw"
                className="rbt-tutor-recruit__image"
              />
            </div>
            <div className="rbt-tutor-recruit__badge">
              <i className="feather-award" aria-hidden="true"></i>
              <span>{t("tutorRecruitment.badgeText", "Credit on every lesson you build")}</span>
            </div>
          </div>

          <div className="rbt-tutor-recruit__copy">
            <span className="rbt-eyebrow">
              <span className="rbt-eyebrow-dot" aria-hidden="true" />
              {t("tutorRecruitment.eyebrow", "For educators & creators")}
            </span>
            <h2 className="rbt-tutor-recruit__title">
              {t("tutorRecruitment.title", "Teach the world something — get credit for it")}
            </h2>
            <p className="rbt-tutor-recruit__desc">
              {t(
                "tutorRecruitment.desc",
                "SmartTech Academy exists because people with real skills decided to give them away for free. If you've got a YouTube channel, a subject you know cold, or a course idea you've always wanted to build, we'll help you turn it into a structured, free course — with your name on every lesson, in front of a learner base that keeps growing."
              )}
            </p>

            <div className="rbt-tutor-recruit__points">
              {points.map((point) => (
                <div className="rbt-tutor-recruit__point" key={point.key}>
                  <span className="rbt-tutor-recruit__point-icon">
                    <i className={point.icon}></i>
                  </span>
                  <span>
                    <strong>{point.label}</strong>
                    <span>{point.desc}</span>
                  </span>
                </div>
              ))}
            </div>

            <button
              type="button"
              className="rbt-btn btn-gradient hover-icon-reverse radius-round"
              onClick={() => setModalOpen(true)}
            >
              <span className="icon-reverse-wrapper">
                <span className="btn-text">{t("tutorRecruitment.applyButton", "Apply to teach")}</span>
                <span className="btn-icon">
                  <i className="feather-arrow-right"></i>
                </span>
                <span className="btn-icon">
                  <i className="feather-arrow-right"></i>
                </span>
              </span>
            </button>
            <span className="rbt-tutor-recruit__note">
              {t("tutorRecruitment.applyNote", "Takes about 2 minutes. A real person reviews every application.")}
            </span>
          </div>
        </div>
      </div>

      <TutorApplicationModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default TutorRecruitment;
